import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useMutationVerifyTwoFactor } from '@/reactQuery/post';
import useAuthStore from '@/store/AuthStore';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useAuthentication from '@/hooks/useAuthentication';

const totpValidationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Invalid code format. Must be 6 digits'),
});

const backupCodeValidationSchema = Yup.object().shape({
  backupCode: Yup.string()
    .required('Backup code is required')
    .matches(/^[a-zA-Z0-9-]{10,14}$/, 'Invalid backup code format'),
});

/**
 * Two Factor Authentication verification form component
 * Allows users to verify their identity using either:
 * 1. A 6-digit TOTP code from an authenticator app
 * 2. A backup recovery code
 */
function TwoFactorVerificationForm() {
  const [activeTab, setActiveTab] = useState<string>('totp');

  // Custom authentication hook
  const { processAuthSuccess, error, setError, navigate } = useAuthentication();

  // Auth store state and actions
  const {
    clearTwoFactorState,
    twoFactorUserId: currentUserId,
    setRequireTwoFactor,
  } = useAuthStore();

  // API mutations for verification
  const { mutateAsync: verifyTwoFactorMutation, isPending } = useMutationVerifyTwoFactor();

  // TOTP form with validation
  const {
    register: registerTotp,
    handleSubmit: handleSubmitTotp,
    formState: { errors: totpErrors, isSubmitting: isSubmittingTotp, isValid: isValidTotp },
  } = useForm({
    defaultValues: { code: '' },
    resolver: yupResolver(totpValidationSchema),
    mode: 'onChange',
  });

  // Backup code form with validation
  const {
    register: backupCodeFormRegister,
    handleSubmit: handleSubmitBackup,
    formState: {
      errors: backupCodeErrors,
      isSubmitting: isSubmittingBackup,
      isValid: isValidBackup,
    },
  } = useForm({
    defaultValues: { backupCode: '' },
    resolver: yupResolver(backupCodeValidationSchema),
    mode: 'onChange',
  });

  /**
   * Verify TOTP code from authenticator app
   * On success, sets auth token and redirects to dashboard
   */
  const verifyTwoFactorCode = async (data: { code: string }) => {
    try {
      setError('');
      if (!currentUserId) {
        setError('User ID is required for verification.');
        return;
      }

      // Call the API to verify the code
      const response = await verifyTwoFactorMutation({
        code: data.code,
        userId: currentUserId,
      });

      // Extract token from response - it could be in response.data.token due to axios wrapping
      const token = response.data?.token || response.token;

      // Set 2FA as completed
      setRequireTwoFactor(false);
      await processAuthSuccess(token, true);
    } catch (err: any) {
      setError(err.data.message || 'Invalid verification code. Please try again.');
    }
  };

  /**
   * Verify using backup recovery code
   * Used when user doesn't have access to their authenticator app
   */
  const verifyWithBackupCode = async (data: { backupCode: string }) => {
    if (!currentUserId) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    try {
      setError('');
      // Call the API to verify the backup code
      const response = await verifyTwoFactorMutation({
        code: data.backupCode,
        userId: currentUserId,
      });

      const token = response.data?.token || response.token;
      // Set 2FA as completed
      setRequireTwoFactor(false);
      await processAuthSuccess(token, true);
    } catch (err: any) {
      setError(err.data.message || 'Invalid backup code. Please try again.');
    }
  };

  /**
   * Cancel verification and go back to login page
   * Clears temporary token and 2FA state
   */
  const handleCancel = () => {
    clearTwoFactorState();
    navigate('/login');
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Two-Factor Verification</h2>

      <p className="mb-6 text-center text-sm text-muted-foreground">
        Please enter the verification code from your authenticator app or use a backup code to
        continue.
      </p>

      <Tabs
        defaultValue="totp"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full mb-4"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="totp">Authenticator App</TabsTrigger>
          <TabsTrigger value="backup">Backup Code</TabsTrigger>
        </TabsList>

        <TabsContent value="totp" className="mt-4">
          <form onSubmit={handleSubmitTotp(verifyTwoFactorCode)}>
            <div className="flex flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative mb-1">
              <label className="text-[#2a3547]">Enter verification code</label>
              <Input
                {...registerTotp('code', {
                  onChange: () => {
                    if (error) setError('');
                  },
                })}
                type="text"
                maxLength={6}
                placeholder="000000"
                className={`text-center text-lg tracking-widest ${
                  totpErrors.code ? 'border-red-600' : ''
                }`}
                autoFocus
              />
              {totpErrors.code?.message && (
                <p className="text-sm text-red-600">{totpErrors.code?.message}</p>
              )}
            </div>

            {error && <p className="text-sm text-red-600 mb-4 font-semibold">{error}</p>}

            <div className="flex gap-3 w-full mt-6 flex-row-reverse">
              <Button
                variant="default"
                type="submit"
                className="flex-1"
                disabled={isSubmittingTotp || !isValidTotp || isPending}
              >
                Verify
              </Button>
              <Button variant="outline" type="button" className="flex-1" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          <form onSubmit={handleSubmitBackup(verifyWithBackupCode)}>
            <div className="flex flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative mb-1">
              <label className="text-[#2a3547]">Enter backup code</label>
              <Input
                {...backupCodeFormRegister('backupCode', {
                  onChange: () => {
                    if (error) setError('');
                  },
                })}
                type="text"
                maxLength={14}
                placeholder="Enter your backup code"
                className={`text-center text-lg ${
                  backupCodeErrors.backupCode ? 'border-red-600' : ''
                }`}
                autoFocus
              />
              {backupCodeErrors.backupCode?.message && (
                <p className="text-sm text-red-600">{backupCodeErrors.backupCode?.message}</p>
              )}
            </div>

            {error && <p className="text-sm text-red-600 mb-4 font-semibold">{error}</p>}

            <div className="flex gap-3 w-full mt-6 flex-row-reverse">
              <Button
                variant="default"
                type="submit"
                className="flex-1"
                disabled={isSubmittingBackup || !isValidBackup}
              >
                Verify
              </Button>
              <Button variant="outline" type="button" className="flex-1" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TwoFactorVerificationForm;
